import type { Chat, ChatMessage, Content, ImportedMessage } from "@rhema/client";
import { Button, DayDivider, MessageBubble, RichText, SystemLine } from "@rhema/ui";
import { type ReactNode, useState } from "react";
import { clockTime, dayKey, dayLabel, firstName, toneFor } from "../data/view";
import { contentParts } from "./MessageContent";
import { useRichRender } from "./rich";

// Chat history imported from WhatsApp. Each import shows as one run, in the order of the original
// times, followed by a line saying who imported it and when. Every bubble carries a mark so it is
// never mistaken for a message sent on Rhema.

export const IMPORTED_MARK = "Imported from WhatsApp";
/** How many imported messages show before "Show earlier" is needed. */
export const IMPORTED_PAGE = 60;

type Batch = Extract<Content, { type: "imported" }>;

export type ImportRun = {
  importId: string;
  /** The first batch, where the run is drawn. */
  anchorId: string;
  messages: ImportedMessage[];
  /** Who imported it. The server says who sent each batch, so no one can import in someone else's name. */
  importerId: string | null;
  /** When the latest batch reached the server. */
  importedAt: string;
};

export function isImportBatch(m: ChatMessage): m is ChatMessage & { content: Batch } {
  return m.content?.type === "imported" && !m.deleted;
}

/**
 * Collects the batches of each import. Batches from the same import that arrive in different pages
 * still join one run, which is drawn where its first batch sits.
 */
export function importRuns(messages: ChatMessage[]): Map<string, ImportRun> {
  const runs = new Map<string, ImportRun>();
  for (const m of messages) {
    if (!isImportBatch(m)) continue;
    const key = `${m.senderId ?? ""}:${m.content.import_id}`;
    const run = runs.get(key);
    if (run) {
      run.messages.push(...m.content.messages);
      run.importedAt = m.createdAt;
    } else {
      runs.set(key, {
        importId: m.content.import_id,
        anchorId: m.id,
        messages: [...m.content.messages],
        importerId: m.senderId,
        importedAt: m.createdAt,
      });
    }
  }
  for (const run of runs.values()) {
    const seen = new Set<string>();
    run.messages = run.messages
      .map((message, i) => ({ message, i }))
      .filter(({ message }) => !seen.has(message.id) && seen.add(message.id))
      .sort((a, b) => a.message.original_time.localeCompare(b.message.original_time) || a.i - b.i)
      .map(({ message }) => message);
  }
  return runs;
}

/** The key importRuns files a batch under. */
export function runKey(m: ChatMessage & { content: Batch }): string {
  return `${m.senderId ?? ""}:${m.content.import_id}`;
}

const OMITTED: Record<string, string> = {
  image: "Photo not included in the export",
  video: "Video not included in the export",
  audio: "Audio not included in the export",
  sticker: "Sticker not included in the export",
  gif: "GIF not included in the export",
  document: "Document not included in the export",
  contact: "Contact card not included in the export",
};

function longDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function senderName(m: ImportedMessage, chat: Chat): string {
  const member = m.sender_id ? chat.members.find((x) => x.user.id === m.sender_id) : undefined;
  return member?.user.display_name ?? m.original_sender ?? "Someone";
}

/** Imported media drawn the way a Rhema photo or file is, reusing the same parts. */
function asChatMessage(m: ImportedMessage, chatId: string): ChatMessage {
  return {
    id: `imported:${m.id}`,
    clientId: null,
    chatId,
    seq: -1,
    senderId: m.sender_id ?? null,
    createdAt: m.original_time,
    kind: "message",
    content: m.media
      ? {
          type: "media",
          media: m.media,
          kind: m.media_kind ?? "file",
          ...(m.body ? { caption: m.body } : {}),
        }
      : null,
    system: null,
    edited: null,
    reactions: {},
    deleted: false,
    status: "sent",
    expiresAt: null,
  };
}

export function ImportedHistory({
  run,
  chat,
  meId,
}: {
  run: ImportRun;
  chat: Chat;
  meId: string | undefined;
}) {
  const [shown, setShown] = useState(IMPORTED_PAGE);
  const total = run.messages.length;
  const visible = run.messages.slice(Math.max(0, total - shown));
  const hidden = total - visible.length;
  const items: ReactNode[] = [];
  const render = useRichRender(chat);

  if (hidden > 0) {
    items.push(
      <Button
        key="earlier"
        variant="ghost"
        size="sm"
        className="mb-1.5 self-center"
        onClick={() => setShown((n) => n + IMPORTED_PAGE * 4)}
      >
        Show {Math.min(hidden, IMPORTED_PAGE * 4)} earlier imported messages
      </Button>,
    );
  }

  let lastDay = "";
  let lastSender: string | null | undefined;
  for (const m of visible) {
    const day = dayKey(m.original_time);
    if (day !== lastDay) {
      lastDay = day;
      lastSender = undefined;
      items.push(<DayDivider key={`day-${m.id}`}>{dayLabel(m.original_time)}</DayDivider>);
    }
    if (m.system) {
      lastSender = undefined;
      items.push(<SystemLine key={m.id}>{m.body}</SystemLine>);
      continue;
    }
    const own = Boolean(meId && m.sender_id === meId);
    const name = senderName(m, chat);
    const newRun = lastSender !== name;
    lastSender = name;
    const time = clockTime(m.original_time);
    const muted = (text: string) => <span className="text-ink-meta italic">{text}</span>;
    const media = m.media ? contentParts(asChatMessage(m, chat.id), time) : null;
    // Imported messages were written without Rhema's formatting, so they show as typed, with
    // web and email addresses made into links.
    let body: ReactNode = m.body ? <RichText body={m.body} {...render} /> : m.body;
    if (m.deleted) body = muted("This message was deleted.");
    else if (m.omitted) {
      const note = OMITTED[m.omitted] ?? "Media not included in the export";
      body = muted(m.body ? `${note}: ${m.body}` : note);
    }
    items.push(
      <div key={m.id} className={`flex flex-col ${newRun ? "mt-2" : ""}`}>
        <MessageBubble
          {...(media ?? {})}
          direction={own ? "out" : "in"}
          time={time}
          note={IMPORTED_MARK}
          edited={Boolean(m.edited)}
          author={!own && newRun ? { name, tone: toneFor(m.sender_id ?? name) } : undefined}
        >
          {media ? media.children : body}
        </MessageBubble>
      </div>,
    );
  }

  const importer =
    run.importerId && run.importerId === meId
      ? "You"
      : firstName(chat.members.find((x) => x.user.id === run.importerId)?.user.display_name ?? "Someone");
  items.push(
    <SystemLine key="imported-by">
      {importer} imported {total.toLocaleString("en-GB")} {total === 1 ? "message" : "messages"} from WhatsApp
      on {longDate(run.importedAt)}
    </SystemLine>,
  );

  return <>{items}</>;
}
